import React, { useState, useEffect } from "react";
import "./EditContributorProfile.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useContributorAuth } from "../../context/ContributorAuthContext";

const EditContributorProfile = () => {
  const [personalWebsite, setPersonalWebsite] = useState("");
  const [experiences, setExperiences] = useState([]);
  const [description, setDescription] = useState([]);
  const [photo, setPhoto] = useState(null);
  const [socialInputs, setSocialInputs] = useState({
    twitter: "",
    instagram: "",
    linkedin: "",
    facebook: "",
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const navigate = useNavigate();
  const { contributor, loading } = useContributorAuth();

  useEffect(() => {
    if (!loading && !contributor) {
      navigate("/loginContributor", { replace: true });
      return;
    }
    if (!loading && contributor) {
      fetchContributorData();
    }
  }, [contributor, loading]);

  const fetchContributorData = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/contributor/get-profile`,
        {
          credentials: "include",
        }
      );
      if (response.ok) {
        const data = await response.json();

        const formattedExperiences = data.experiences.map((exp) => ({
          ...exp,
          date: formatDate(exp.date),
        }));

        // Update state with fetched data
        setPersonalWebsite(data.personalWebsite || "");
        setExperiences(formattedExperiences || []);
        setDescription(data.publications[0]?.description || "");
        setPhoto(data.photo || null);
        setSocialInputs({
          twitter: data.socialInputs.twitter || "",
          instagram: data.socialInputs.instagram || "",
          linkedin: data.socialInputs.linkedin || "",
          facebook: data.socialInputs.facebook || "",
        });
        } else {
        toast.error("Failed to fetch contributor data");
      }
    } catch (error) {
      console.error("Error fetching contributor data:", error);
      toast.error("An error occurred while fetching your profile. Please try again.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/contributor/update-profile`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            experiences,
            description,
            personalWebsite,
            twitter: socialInputs.twitter,
            instagram: socialInputs.instagram,
            linkedin: socialInputs.linkedin,
            facebook: socialInputs.facebook,
          }),
        }
      );
      if (response.ok) {
        toast.success("Profile updated successfully!");
        navigate("/contributor-dashboard");
      } else {
        const errorData = await response.json();
        toast.error(`Error updating profile: ${errorData.message}`);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred while updating your profile. Please try again.");
    }
  };

  const addExperience = () => {
    setExperiences([
      ...experiences,
      {
        id: Date.now(),
        title: "",
        genre: "",
        publisher: "",
        date: "",
        url: "",
      },
    ]);
  };

  const updateExperience = (id, field, value) => {
    setExperiences(
      experiences.map((exp) =>
        exp.id === id
          ? { ...exp, [field]: field === "date" ? formatDate(value) : value }
          : exp
      )
    );
  };

  const removeExperience = (id) => {
    setExperiences(experiences.filter((exp) => exp.id !== id));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(URL.createObjectURL(file));
    }
  };

  const handleSocialInputChange = (field, value) => {
    setSocialInputs({ ...socialInputs, [field]: value });
  };

  return (
    <div className="min-h-screen bg-[#f0f2f5] py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <img
            src="/logo.png"
            alt="Logo"
            className="h-[50px] sm:h-[60px] mx-auto mb-4 sm:mb-6 cursor-pointer hover:opacity-80 transition-opacity drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]"
            onClick={() => navigate(-1)}
          />
          <h1 className="text-2xl sm:text-3xl font-bold text-[#3498db] mb-2">
            Edit Contributor Profile
          </h1>
          <p className="text-[#57606f] mt-2 text-sm sm:text-base">
            Update your profile information below
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white shadow-[0_10px_40px_rgba(0,0,0,0.1),0_1px_3px_rgba(0,0,0,0.05)] rounded-2xl p-4 sm:p-8 space-y-6 sm:space-y-8 backdrop-blur-[10px] border border-white/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="lg:col-span-2 space-y-4 sm:space-y-6">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Writer Experience <span className="text-red-500">*</span></h2>
              {experiences.map((exp, index) => (
                <div key={exp.id} className="border border-[#ddd] rounded-lg p-4 mb-4 space-y-4">
                  <h3 className="text-base font-medium text-gray-700">Work Experience {index + 1} <span className="text-gray-500 text-sm">(Fill at least one)</span></h3>
                  <input
                    type="text"
                    placeholder="Enter Title"
                    value={exp.title}
                    onChange={(e) =>
                      updateExperience(exp.id, "title", e.target.value)
                    }
                    className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  />
                  <input
                    type="text"
                    placeholder="Enter Genre or Topic"
                    value={exp.genre}
                    onChange={(e) =>
                      updateExperience(exp.id, "genre", e.target.value)
                    }
                    className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  />
                  <input
                    type="text"
                    placeholder="Enter Publisher/Publication"
                    value={exp.publisher}
                    onChange={(e) =>
                      updateExperience(exp.id, "publisher", e.target.value)
                    }
                    className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  />
                  <input
                    type="date"
                    placeholder="Enter date"
                    value={exp.date}
                    onChange={(e) =>
                      updateExperience(exp.id, "date", e.target.value)
                    }
                    className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  />
                  <input
                    type="url"
                    placeholder="Enter Publication URL"
                    value={exp.url}
                    onChange={(e) =>
                      updateExperience(exp.id, "url", e.target.value)
                    }
                    className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  />
                  <button
                    type="button"
                    onClick={() => removeExperience(exp.id)}
                    className="w-full h-11 px-4 bg-[#dc3545] text-white rounded-lg hover:bg-[#c82333] transition-all duration-300 font-semibold shadow-[0_4px_15px_rgba(220,53,69,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(220,53,69,0.4)] active:scale-100"
                  >
                    Remove Experience
                  </button>
                </div>
              ))}

              <button
                type="button"
                onClick={addExperience}
                className="w-full h-11 px-4 bg-[#3498db] text-white rounded-lg hover:bg-[#2980b9] transition-all duration-300 font-semibold shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100 mb-4"
              >
                + Add more experience
              </button>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description of the Writer <span className="text-red-500">*</span></label>
                <textarea
                  placeholder="Description of the Writer"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows="4"
                  className="w-full px-3 py-2 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Personal website <span className="text-gray-500">(Optional)</span></label>
                <input
                  type="url"
                  placeholder="Enter personal website URL"
                  value={personalWebsite}
                  onChange={(e) => setPersonalWebsite(e.target.value)}
                  className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                />
              </div>

              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mt-6 mb-4">Digital Presence <span className="text-gray-500 text-sm font-normal">(Optional)</span></h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Twitter"
                  value={socialInputs.twitter}
                  onChange={(e) =>
                    handleSocialInputChange("twitter", e.target.value)
                  }
                  className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                />
                <input
                  type="text"
                  placeholder="Instagram"
                  value={socialInputs.instagram}
                  onChange={(e) =>
                    handleSocialInputChange("instagram", e.target.value)
                  }
                  className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                />
                <input
                  type="text"
                  placeholder="LinkedIn"
                  value={socialInputs.linkedin}
                  onChange={(e) =>
                    handleSocialInputChange("linkedin", e.target.value)
                  }
                  className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                />
                <input
                  type="text"
                  placeholder="Facebook"
                  value={socialInputs.facebook}
                  onChange={(e) =>
                    handleSocialInputChange("facebook", e.target.value)
                  }
                  className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-6">
                <button
                  type="submit"
                  className="flex-1 h-11 bg-[#3498db] text-white rounded-lg hover:bg-[#2980b9] transition-all duration-300 font-semibold uppercase tracking-[0.5px] shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 text-sm sm:text-base"
                >
                  Update Profile
                </button>
                <button
                  type="button"
                  className="flex-1 h-11 bg-white text-[#3498db] border border-[#3498db] rounded-lg hover:bg-[#f0f8ff] transition-all duration-300 font-semibold hover:scale-105 active:scale-100 text-sm sm:text-base"
                  onClick={() => navigate("/contributor-dashboard")}
                >
                  Back to Dashboard
                </button>
              </div>
            </div>

            <div className="lg:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Profile Photo <span className="text-gray-500">(Optional)</span></label>
              <input
                type="file"
                id="photo-upload"
                accept="image/*"
                onChange={handlePhotoUpload}
                style={{ display: "none" }}
              />
              <label htmlFor="photo-upload" className="flex flex-col items-center justify-center w-full h-48 border-2 border-[#ddd] border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors overflow-hidden">
                {photo ? (
                  <img
                    src={photo}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center">
                    <i className="fas fa-camera text-gray-500 text-3xl mb-2"></i>
                    <span className="text-sm text-gray-500">Upload photo</span>
                  </div>
                )}
              </label>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditContributorProfile;
